import { CalendarEventData } from '@/Domain/api/Conversation/dto/Message.dto';

type Props = {
  data: CalendarEventData;
};

const formatDateTimeParts = (
  startIso: string,
  endIso: string | null,
): { date: string; time: string } => {
  const start = new Date(startIso);
  if (isNaN(start.getTime())) return { date: startIso, time: '' };

  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  const date = dateFmt.format(start);
  const startTime = timeFmt.format(start);
  if (!endIso) return { date, time: startTime };
  const end = new Date(endIso);
  if (isNaN(end.getTime())) return { date, time: startTime };
  return { date, time: `${startTime} – ${timeFmt.format(end)}` };
};

const locationLabel = (location: string | null, meetingUrl: string | null): string | null => {
  const candidate = location || meetingUrl;
  if (!candidate) return null;
  if (/meet\.google\.com/i.test(candidate)) return 'Google Meet';
  if (/zoom\.us|zoom\.com/i.test(candidate)) return 'Zoom';
  if (/teams\.(microsoft|live)\.com/i.test(candidate)) return 'Microsoft Teams';
  if (/^https?:\/\//.test(candidate)) return 'Online meeting';
  return candidate;
};

export const InboxMeetingBooked = ({ data }: Props) => {
  const { title, start_iso, end_iso, location, meeting_url } = data;
  const { date, time } = formatDateTimeParts(start_iso, end_iso);
  const label = locationLabel(location, meeting_url);

  return (
    <div
      // mb-2: Inbox's MessageItem has no gap between siblings (widget does).
      className="mb-2 flex w-full max-w-[340px] items-stretch gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm"
      data-testid="inbox-meeting-booked-card"
    >
      <div className="my-0.5 w-1 shrink-0 self-stretch rounded-full bg-success" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs text-muted-foreground" data-testid="inbox-meeting-time">
            {date}
          </p>
          {time && <p className="shrink-0 text-xs font-medium text-success">{time}</p>}
        </div>
        <p
          className="line-clamp-1 text-sm font-semibold text-foreground"
          data-testid="inbox-meeting-title"
        >
          {title}
        </p>
        {label && (
          <p className="text-xs text-muted-foreground" data-testid="inbox-meeting-location">
            {label}
          </p>
        )}
      </div>
    </div>
  );
};
